import { useMemo } from 'react'
import { TrendIndicator } from 'src/components/analytics/common/TrendIndicator'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { useChartData } from 'src/components/overview/useChartData'
import { ChartSkeleton } from '../common/Skeleton'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'

const EMPTY_CONVERSION_RATE_DATA = {
  nonGuestRate: 0,
  prefilledAddressPercentageValue: 0,
  prefilledAddressPercentageComparedValue: 0,
  prefilledAddressCountValue: 0,
  prefilledLoginCountValue: 0,
  prefilledAddressCountPercentageDifference: 0,
  nonGuestRatePercentageDifference: 0
}

export default function AddressPrefillRate() {
  const {
    getters: { refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.conversion_address_prefilled_rate,
      isNoComparison: true,
      dependencies: {
        endpoints: ['/prefilled-address-timescale', '/prefilled-address']
      }
    },
    refreshKey
  })

  const conversionRateData: {
    prefilledAddressPercentageValue: number
    prefilledAddressCountValue: number
    prefilledLoginCountValue: number
    prefilledAddressCountPercentageDifference: number
  } = useMemo(() => {
    const prefilledAddressData = data[1]?.data?.result?.widgetData?.data
    const prefilledAddressComparedData = data[1]?.data?.result?.widgetData?.compared_data

    if (!prefilledAddressData || prefilledAddressData.length === 0) {
      return EMPTY_CONVERSION_RATE_DATA
    }

    const [prefilledAddressPercentage, prefilledLoginCount, prefilledAddressCount] = prefilledAddressData
    const [prefilledAddressPercentageCompared] = prefilledAddressComparedData

    const prefilledAddressPercentageValue = prefilledAddressPercentage.data[0].x
    const prefilledAddressCountValue = prefilledAddressCount.data[0].x
    const prefilledLoginCountValue = prefilledLoginCount.data[0].x

    let prefilledAddressPercentageComparedValue = ''
    if (prefilledAddressPercentageCompared) {
      prefilledAddressPercentageComparedValue = prefilledAddressPercentageCompared.data[0].x
    }

    let prefilledAddressCountPercentageDifference = 0
    if (prefilledAddressPercentageCompared) {
      prefilledAddressCountPercentageDifference = parseFloat(
        (parseFloat(prefilledAddressPercentageValue) - parseFloat(prefilledAddressPercentageComparedValue)).toFixed(2)
      )
    }

    return {
      prefilledAddressPercentageValue,
      prefilledAddressCountValue,
      prefilledLoginCountValue,
      prefilledAddressCountPercentageDifference
    }
  }, [data])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else {
      return (
        <div className='flex flex-col gap-2 mt-4 h-full'>
          <div className='flex justify-between items-center bg-gray-lightest rounded-md p-4 text-sm h-1/3'>
            <p className='text-sm'>Sessions with address pre-filled</p>
            <div className='flex items-center gap-2'>
              <span className='font-medium'>
                <span className='text-carbon text-lg'>{conversionRateData.prefilledAddressCountValue}</span> out of{' '}
                <span className='text-carbon text-lg'>{conversionRateData.prefilledLoginCountValue}</span>
              </span>
              <TrendIndicator
                value={conversionRateData.prefilledAddressCountPercentageDifference}
                formatter={value => `${Math.abs(value)}%`}
              />
            </div>
          </div>
          <div className='flex justify-between items-center bg-gray-lightest rounded-md p-4 text-sm h-1/3'>
            <p className='text-sm'>Percentage of sessions with pre-filled address</p>
            <div className='flex items-center gap-2'>
              <span className='font-medium'>
                <span className='text-carbon text-lg'>{conversionRateData.prefilledAddressPercentageValue}</span>%
              </span>
              <TrendIndicator
                value={conversionRateData.prefilledAddressCountPercentageDifference}
                formatter={value => `${Math.abs(value)}%`}
              />
            </div>
          </div>
        </div>
      )
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}
